import React, {useState} from 'react';
import {view, Icon, Action} from '@sail/ui';
import {edit} from '@sail/icons/react/Icon';
import {FormattedMessage, useIntl} from 'react-intl';
import {getStore, setValue} from 'src/v2/state/store';
import {Text} from 'src/v2/components/common/Text';
import {COLORS} from 'src/v2/util/color';
import {useUpdateRefundAddress} from 'src/v2/components/directDeposit/useUpdateRefundAddress';
import {handleError} from 'src/v2/components/error/handleError';

import {RefundAddressDialog} from 'src/v2/components/directDeposit/RefundAddressDialog';
import {messages} from 'src/v2/components/directDeposit/refundAddressMessages';

export const RefundAddressSection = () => {
  const intl = useIntl();
  const store = getStore();
  const [updateRefundAddress, {loading}] = useUpdateRefundAddress();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const {
    refundMethod,
    customRefundAddress,
    isRefundAddressApplied,
    selectedNetwork,
    livemode,
  } = store.useSelector((state) => ({
    refundMethod: state.directDeposit.refundMethod,
    customRefundAddress: state.directDeposit.customRefundAddress,
    isRefundAddressApplied: state.directDeposit.isRefundAddressApplied,
    selectedNetwork: state.directDeposit.selectedDirectDepositNetwork,
    livemode: state.livemode,
  }));

  const handleApply = async ({
    refundMethod: newRefundMethod,
    customRefundAddress: newCustomRefundAddress,
  }: {
    refundMethod: 'sendToPaymentAddress' | 'setNewRefundAddress';
    customRefundAddress: string;
  }) => {
    console.log('[RefundAddressSection] handleApply called', {
      newRefundMethod,
      newCustomRefundAddress,
      selectedNetwork,
      loading,
    });

    if (newRefundMethod === 'setNewRefundAddress') {
      if (!newCustomRefundAddress?.trim() || loading) {
        console.log('[RefundAddressSection] Validation failed or loading', {
          hasAddress: !!newCustomRefundAddress?.trim(),
          loading,
        });
        return;
      }

      try {
        console.log('[RefundAddressSection] Calling updateRefundAddress...');
        await updateRefundAddress({
          refundAddress: newCustomRefundAddress,
          destinationNetwork: selectedNetwork,
        });
        console.log('[RefundAddressSection] updateRefundAddress succeeded');

        setValue((state) => {
          state.directDeposit.refundMethod = newRefundMethod;
          state.directDeposit.customRefundAddress = newCustomRefundAddress;
          state.directDeposit.isRefundAddressApplied = true;
        });

        setIsDialogOpen(false);
      } catch (error) {
        console.error(
          '[RefundAddressSection] updateRefundAddress failed',
          error,
        );
        handleError({error, intl, context: 'direct_deposit'});
      }
    } else {
      // sendToPaymentAddress — clear refund address on backend
      if (loading) {
        return;
      }

      try {
        await updateRefundAddress({
          refundAddress: null,
          destinationNetwork: selectedNetwork,
        });

        setValue((state) => {
          state.directDeposit.refundMethod = newRefundMethod;
          state.directDeposit.customRefundAddress = '';
          state.directDeposit.isRefundAddressApplied = false;
        });

        setIsDialogOpen(false);
      } catch (error) {
        handleError({error, intl, context: 'direct_deposit'});
      }
    }
  };

  return (
    <view.div css={{stack: 'y', gap: 'xxsmall'}}>
      {/* Label */}
      <Text type="body" css={{color: COLORS.textDisabled}}>
        <FormattedMessage {...messages.preferredRefundAddress} />
      </Text>

      {/* Read-only refund method display */}
      <view.div
        css={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'medium',
          paddingRight: 'small',
        }}
      >
        <Text
          type="bodyEmphasized"
          css={{
            color: COLORS.textPrimary,
            flex: 1,
            wordBreak: 'break-all',
          }}
        >
          {refundMethod === 'sendToPaymentAddress' ? (
            <FormattedMessage {...messages.sendToPaymentAddress} />
          ) : null}
          {refundMethod !== 'sendToPaymentAddress' && isRefundAddressApplied
            ? customRefundAddress
            : null}
          {refundMethod !== 'sendToPaymentAddress' &&
          !isRefundAddressApplied ? (
            <FormattedMessage {...messages.setNewRefundAddress} />
          ) : null}
        </Text>

        <Action onPress={() => setIsDialogOpen(true)}>
          <Icon
            icon={edit}
            size="small"
            css={{cursor: 'pointer', color: COLORS.textSubduedOnLight}}
          />
        </Action>
      </view.div>

      <RefundAddressDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onApply={handleApply}
        initialRefundMethod={refundMethod}
        initialCustomRefundAddress={customRefundAddress}
        selectedNetwork={selectedNetwork}
        livemode={livemode}
        loading={loading}
      />
    </view.div>
  );
};
